import { CreatorMetricsTimeline } from '~/hooks/api/useGetCreatorMetricsTimeline';

/**
 * Design system color tokens for the chart
 * Using Tailwind CSS variables from the design system
 */
export const CHART_COLORS = {
  /** Primary chart color - blue-300 from design tokens */
  primary: 'var(--wb-color-blue-300)',
  /** Positive growth indicator - green-500 from design tokens */
  positive: 'var(--wb-color-green-500)',
  /** Negative growth indicator - red-500 from design tokens */
  negative: 'var(--wb-color-red-500)',
} as const;

/**
 * Resolves a CSS color string — including ones that contain `var(...)` — to a
 * concrete `rgb(...)` value the browser can apply to SVG presentation
 * attributes like `fill` and `stroke`. Recharts forwards these props as raw
 * SVG attributes, and SVG attributes do not evaluate `var()` the way CSS
 * properties do, so a chart fed `var(--token)` directly renders invisibly.
 *
 * Uses a hidden probe element so the browser performs the full cascade
 * resolution (including the var()'s declared fallback) for us. Returns the
 * input unchanged on the server or when no `var()` is present.
 */
export const resolveCssColor = (color: string): string => {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return color;
  }
  if (!color.includes('var(')) {
    return color;
  }
  const probe = document.createElement('span');
  probe.style.display = 'none';
  probe.style.color = color;
  document.body.appendChild(probe);
  const resolved = window.getComputedStyle(probe).color;
  document.body.removeChild(probe);
  return resolved || color;
};

const WHITE_COLOR_VALUES = new Set([
  '#fff',
  '#ffffff',
  'white',
  'rgb(255, 255, 255)',
  'rgb(255 255 255)',
  'rgba(255, 255, 255, 1)',
  'hsl(0, 0%, 100%)',
]);

const isWhiteColor = (color: string): boolean =>
  WHITE_COLOR_VALUES.has(color.trim().toLowerCase());

export const getVisibleChartColor = (
  primary: string,
  secondary: string,
): string => (isWhiteColor(primary) ? secondary : primary);

/**
 * Individual data point for chart rendering
 */
export interface ChartDataPoint {
  /** Month name (e.g., "January") */
  month: string;
  /** Growth rate as a decimal (e.g., 0.05 for 5%) */
  growthRate: number | null;
  /** Absolute follower growth count */
  followersGrowth: number | null;
  /** Total follower count at this point */
  followers: number | null;
  /** Month-over-month growth rate (same as growthRate) */
  mom: number | null;
}

/**
 * Calculated latest metrics for the summary display
 */
export interface LatestMetrics {
  /** Current follower count */
  followers: number;
  /**
   * Absolute follower delta across the displayed range. Null when no
   * meaningful follower data exists.
   */
  growth: number | null;
  /**
   * Range growth rate formatted to one decimal place (e.g. "12.5"). Null when
   * no meaningful follower data exists.
   */
  growthRate: string | null;
}

type MonthlyEntry = NonNullable<
  CreatorMetricsTimeline['timeline']['MONTHLY']
>[number];

/**
 * Drops months at the start of the timeline that have no real follower data
 * (0 or null). Mid-timeline zeros are preserved — a creator dropping to 0
 * followers is valid data we want to display.
 */
const trimLeadingEmptyMonths = (
  monthlyData: MonthlyEntry[],
): MonthlyEntry[] => {
  const firstMeaningfulIndex = monthlyData.findIndex(
    (item) => item.metrics.followers != null && item.metrics.followers > 0,
  );
  if (firstMeaningfulIndex === -1) return [];
  return monthlyData.slice(firstMeaningfulIndex);
};

/**
 * Generates all months between two dates (inclusive) as Date objects
 * set to the 1st of each month in UTC.
 */
const generateMonthRange = (start: Date, end: Date): Date[] => {
  const months: Date[] = [];
  const current = new Date(
    Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), 1),
  );
  const last = new Date(Date.UTC(end.getUTCFullYear(), end.getUTCMonth(), 1));

  while (current <= last) {
    months.push(new Date(current));
    current.setUTCMonth(current.getUTCMonth() + 1);
  }

  return months;
};

const monthKey = (date: Date): string =>
  `${date.getUTCFullYear()}-${String(date.getUTCMonth()).padStart(2, '0')}`;

/**
 * Transforms raw timeline data into chart-compatible format.
 * Fills in missing months with null values so the X axis remains
 * evenly spaced even when the API returns gaps. Leading months with
 * no real follower data (followers is 0 or null) are trimmed so the
 * chart starts at the first meaningful data point.
 */
export const transformTimelineToChartData = (
  timeline: CreatorMetricsTimeline['timeline'],
): ChartDataPoint[] => {
  const monthlyData = timeline.MONTHLY ?? [];

  if (monthlyData.length === 0) return [];

  // Trim leading months with no real data so the x-axis starts at the
  // first month where the creator had followers. Without this, the area
  // chart visually implies growth started later than it actually did.
  // Mid-timeline zeros (a creator dropping to 0) are preserved.
  const meaningfulData = trimLeadingEmptyMonths(monthlyData);
  if (meaningfulData.length === 0) return [];

  // Build a lookup keyed by "YYYY-MM" for O(1) access
  const dataByMonth = new Map(
    meaningfulData.map((item) => {
      const d = new Date(item.date);
      return [monthKey(d), item];
    }),
  );

  const firstDate = new Date(meaningfulData[0].date);
  const lastDate = new Date(meaningfulData[meaningfulData.length - 1].date);
  const allMonths = generateMonthRange(firstDate, lastDate);

  return allMonths.map((date) => {
    const key = monthKey(date);
    const item = dataByMonth.get(key);

    const month = date.toLocaleDateString('en-US', {
      month: 'long',
      timeZone: 'UTC',
    });

    if (item) {
      return {
        month,
        growthRate: item.metrics.followers_growth_rate,
        followersGrowth: item.metrics.followers_growth,
        followers: item.metrics.followers,
        mom: item.metrics.followers_growth_rate,
      };
    }

    // Missing month — insert a placeholder with null values
    return {
      month,
      growthRate: null,
      followersGrowth: null,
      followers: null,
      mom: null,
    };
  });
};

const EMPTY_METRICS: LatestMetrics = {
  followers: 0,
  growth: null,
  growthRate: null,
};

/**
 * Calculates summary metrics from timeline data. Uses the first and latest
 * follower counts directly to represent growth across the displayed range.
 * Leading zero-follower months are ignored so a creator who started from 0
 * doesn't have their summary pinned to 0%; mid-timeline zeros are kept so a
 * drop-to-zero shows as real data.
 */
export const calculateLatestMetrics = (
  timeline: CreatorMetricsTimeline['timeline'],
): LatestMetrics => {
  const monthlyData = timeline.MONTHLY ?? [];
  const meaningfulData = trimLeadingEmptyMonths(monthlyData);
  if (meaningfulData.length === 0) return EMPTY_METRICS;

  const first = meaningfulData[0];
  const latest = meaningfulData[meaningfulData.length - 1];
  const firstFollowers = first.metrics.followers ?? 0;
  const latestFollowers = latest.metrics.followers ?? 0;
  const growth = latestFollowers - firstFollowers;
  const growthRate = firstFollowers > 0 ? (growth / firstFollowers) * 100 : 0;

  return {
    followers: latestFollowers,
    growth,
    growthRate: growthRate.toFixed(1),
  };
};
